export const COURT_WIDTH = 80;
export const COURT_HEIGHT = 26;
export const PADDLE_HEIGHT = 5;
export const PADDLE_SPEED = 10;
export const WINNING_SCORE = 5;

export type GameMode = "title" | "playing" | "paused" | "gameover" | "stopped";
export type GameInput = "start" | "up" | "down" | "exit" | "pause";
export type GameEvent = "wall" | "paddle" | "point" | "win" | "loss";

export interface GameState {
  width: number;
  height: number;
  mode: GameMode;
  playerY: number;
  opponentY: number;
  playerScore: number;
  opponentScore: number;
  ballX: number;
  ballY: number;
  ballVx: number;
  ballVy: number;
  winner: string;
  bestRally: number;
  rally: number;
  seed: number;
  aiError: number;
}

export interface StepResult {
  state: GameState;
  events: GameEvent[];
}

export interface PaddleDamage {
  left: readonly number[];
  right: readonly number[];
}

function random(seed: number): [number, number] {
  let value = seed | 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return [value >>> 0, (value >>> 0) / 0x1_0000_0000];
}

function centredPaddle(height: number): number {
  return Math.floor((height - PADDLE_HEIGHT) / 2);
}

function serve(state: GameState, direction?: -1 | 1): GameState {
  const [seed1, choice] = random(state.seed);
  const [seed2, speed] = random(seed1);
  const [seed3, error] = random(seed2);
  const side = direction ?? (choice < 0.5 ? -1 : 1);
  return {
    ...state,
    seed: seed3,
    ballX: state.width / 2,
    ballY: state.height / 2,
    ballVx: 18 * side,
    ballVy: (choice < 0.5 ? -1 : 1) * (5 + speed * 3),
    aiError: -4.5 + error * 9,
    rally: 0,
  };
}

export function createGame(
  width = COURT_WIDTH,
  height = COURT_HEIGHT,
  seed = 0x5eed,
): GameState {
  const initial: GameState = {
    width,
    height,
    mode: "title",
    playerY: centredPaddle(height),
    opponentY: centredPaddle(height),
    playerScore: 0,
    opponentScore: 0,
    ballX: width / 2,
    ballY: height / 2,
    ballVx: 18,
    ballVy: 6,
    winner: "",
    bestRally: 0,
    rally: 0,
    seed,
    aiError: 0,
  };
  return serve(initial, 1);
}

export function movePaddle(
  state: GameState,
  side: "player" | "opponent",
  direction: -1 | 0 | 1,
  distance = 2,
): GameState {
  const key = side === "player" ? "playerY" : "opponentY";
  const bottom = state.height - PADDLE_HEIGHT - 1;
  return {
    ...state,
    [key]: Math.max(1, Math.min(bottom, state[key] + direction * distance)),
  };
}

export function applyInput(state: GameState, input: GameInput): GameState {
  if (input === "start") {
    if (["title", "gameover", "stopped"].includes(state.mode)) {
      const reset = {
        ...state,
        mode: "playing" as const,
        playerY: centredPaddle(state.height),
        opponentY: centredPaddle(state.height),
        playerScore: 0,
        opponentScore: 0,
        winner: "",
      };
      return serve(reset);
    }
    if (state.mode === "paused") return { ...state, mode: "playing" };
  }
  if (input === "exit") {
    if (state.mode === "playing" || state.mode === "paused") {
      return {
        ...state,
        mode: "title",
        playerScore: 0,
        opponentScore: 0,
        winner: "",
      };
    }
    return { ...state, mode: "stopped" };
  }
  if (input === "pause" && state.mode === "playing") {
    return { ...state, mode: "paused" };
  }
  if (state.mode !== "playing") return state;
  if (input === "up") return movePaddle(state, "player", -1);
  if (input === "down") return movePaddle(state, "player", 1);
  return state;
}

function paddleBounce(state: GameState, paddleY: number, contactY = state.ballY): GameState {
  const centre = paddleY + (PADDLE_HEIGHT - 1) / 2;
  const offset = (contactY - centre) / (PADDLE_HEIGHT / 2);
  const rally = state.rally + 1;
  return {
    ...state,
    ballVy: Math.max(-12, Math.min(12, state.ballVy + offset * 5)),
    rally,
    bestRally: Math.max(state.bestRally, rally),
  };
}

export function loseLife(state: GameState, player: boolean): StepResult {
  const playerScore = state.playerScore + (player ? 1 : 0);
  const opponentScore = state.opponentScore + (player ? 0 : 1);
  const lost = (player ? playerScore : opponentScore) >= WINNING_SCORE;
  let next = serve(
    { ...state, playerScore, opponentScore },
    player ? -1 : 1,
  );
  if (lost) {
    next = {
      ...next,
      mode: "gameover",
      winner: player ? "JAY WINS" : "YOU WIN!",
    };
  }
  return {
    state: next,
    events: ["point", ...(lost ? [player ? "loss" : "win"] as GameEvent[] : [])],
  };
}

export function paddleSegmentAt(ballY: number, paddleY: number): number | null {
  if (ballY < paddleY - 0.5 || ballY > paddleY + PADDLE_HEIGHT - 0.5) return null;
  return Math.max(0, Math.min(PADDLE_HEIGHT - 1, Math.floor(ballY - paddleY + 0.5)));
}

export function projectedBallY(state: GameState, elapsed: number): number {
  const top = 1;
  const bottom = state.height - 2;
  let y = state.ballY + state.ballVy * elapsed;
  while (y < top || y > bottom) {
    if (y < top) y = top * 2 - y;
    if (y > bottom) y = bottom * 2 - y;
  }
  return y;
}

export function stepGame(
  source: GameState,
  elapsed: number,
  withAi = true,
  damage: PaddleDamage = { left: [], right: [] },
): StepResult {
  if (source.mode !== "playing") return { state: source, events: [] };
  const seconds = Math.max(0, Math.min(0.1, elapsed));
  const events: GameEvent[] = [];
  let state = {
    ...source,
    ballX: source.ballX + source.ballVx * seconds,
    ballY: source.ballY + source.ballVy * seconds,
  };

  if (state.ballY <= 1) {
    state = { ...state, ballY: 2 - state.ballY, ballVy: Math.abs(state.ballVy) };
    events.push("wall");
  } else if (state.ballY >= state.height - 2) {
    state = {
      ...state,
      ballY: 2 * (state.height - 2) - state.ballY,
      ballVy: -Math.abs(state.ballVy),
    };
    events.push("wall");
  }

  const horizontalDistance = state.ballX - source.ballX;
  const leftRatio = horizontalDistance === 0 ? 0 : (3 - source.ballX) / horizontalDistance;
  const leftCollisionY = projectedBallY(source, seconds * leftRatio);
  const leftSegment = paddleSegmentAt(leftCollisionY, state.playerY);
  const rightX = state.width - 4;
  const rightRatio = horizontalDistance === 0 ? 0 : (rightX - source.ballX) / horizontalDistance;
  const rightCollisionY = projectedBallY(source, seconds * rightRatio);
  const rightSegment = paddleSegmentAt(rightCollisionY, state.opponentY);
  if (
    state.ballVx < 0 && source.ballX >= 3 && state.ballX <= 3 &&
    leftSegment !== null && !damage.left.includes(leftSegment)
  ) {
    state = paddleBounce(
      { ...state, ballX: 3, ballVx: Math.abs(state.ballVx) * 1.025 },
      state.playerY,
      leftCollisionY,
    );
    events.push("paddle");
  } else if (
    state.ballVx > 0 && source.ballX <= rightX && state.ballX >= rightX &&
    rightSegment !== null && !damage.right.includes(rightSegment)
  ) {
    state = paddleBounce({
      ...state,
      ballX: rightX,
      ballVx: -Math.abs(state.ballVx) * 1.025,
    }, state.opponentY, rightCollisionY);
    events.push("paddle");
  }

  if (state.ballX < 0) {
    const result = loseLife(state, true);
    return { ...result, events: [...events, ...result.events] };
  }
  if (state.ballX >= state.width) {
    const result = loseLife(state, false);
    return { ...result, events: [...events, ...result.events] };
  }

  if (withAi && state.ballVx > 0 && state.ballX > state.width * 0.38) {
    const target = state.ballY - PADDLE_HEIGHT / 2 + state.aiError;
    const distance = target - state.opponentY;
    if (Math.abs(distance) > 0.35) {
      state = movePaddle(state, "opponent", Math.sign(distance) as -1 | 1, Math.min(Math.abs(distance), 6.5 * seconds));
    }
  }
  return { state, events };
}
